import { UcoastEl } from '@/scripts/core/UcoastEl'
import { qsRequired } from '@/scripts/core/global'

interface BundleVariant {
  sku: string
  title: string
  default: boolean
  enabled: boolean
  variantId: string
  variantGid: string
  fakeSoldOut: boolean
}

interface BundleProduct {
  order: number
  title: string
  category: string
  imageSrc: string
  quantity: string
  variants: BundleVariant[]
  productId: string
  productGid: string
  productHandle: string
}

interface BundleCategory {
  id: string
  name: string
  order: number
  products: BundleProduct[]
}

interface DiscountTier {
  discount: number
  minQuantity: number
}

interface BundleDiscount {
  id: string
  tiers?: DiscountTier[]
  title: string
  endDate?: string | null
  discount: number
  products: any[]
  startDate: string
  discountType: 'tierDiscount' | 'allProducts'
}

interface KitData {
  parent: {
    sku: string
    title: string
    category: string
    imageSrc?: string
    productId: string
    variantId: string
    productGid: string
    variantGid: string
    productHandle: string
  }
  content: BundleCategory[]
  discount?: BundleDiscount
}

interface DefaultVariantInfo {
  variantId: string
  price: number
  quantity: number
  productId: string
  productHandle: string
}

interface CartItem {
  id: string
  quantity: number
  properties: {
    _bundleId: string
    _quickAdd: string
    _uniqueId: string
    _version: string
  }
}

export class BundleQuickAdd extends UcoastEl {
  static htmlSelector = 'bundle-quick-add'

  private kitData: KitData
  private batchData: any[] = []
  private form!: HTMLFormElement
  private submitButton!: HTMLButtonElement
  private priceContainer!: HTMLElement

  constructor() {
    super()
    
    const kitDataString = this.getAttribute('data-kit') || '{}'
    
    try {
      this.kitData = JSON.parse(kitDataString)
    } catch (error) {
      console.error('Error parsing kit data:', error)
      this.kitData = { parent: {} as any, content: [] }
    }

    // Parse batch data from attributes
    this.parseBatchData()
  }

  private parseBatchData(): void {
    // Read batch data from data-batch1, data-batch2, etc.
    for (let i = 1; i <= 5; i++) {
      const batchDataString = this.getAttribute(`data-batch${i}`) || '[]'
      try {
        const batchProducts = JSON.parse(batchDataString)
        if (Array.isArray(batchProducts) && batchProducts.length > 0) {
          this.batchData.push(...batchProducts)
        }
      } catch (error) {
        console.error(`Error parsing batch${i} data:`, error)
      }
    }
  }

  override connectedCallback() {
    super.connectedCallback()
    this.init()
  }

  private init(): void {
    this.form = qsRequired('form', this)
    this.submitButton = qsRequired('button[type="submit"]', this)
    this.priceContainer = qsRequired('.bundle-price-display', this)

    this.setupEventListeners()
    this.updatePrice()
  }

  private setupEventListeners(): void {
    // Handle form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault()
      this.submitBundle()
    })

    // Handle quick-add button clicks (if any)
    this.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.classList.contains('quick-add__submit')) {
        e.preventDefault()
        this.updatePrice()
      }
    })
  }

  private updatePrice(): void {
    // Extract default variants with their quantities
    const defaultVariants = this.extractDefaultVariants()
    
    if (defaultVariants.length === 0) {
      console.warn('No default variants found for bundle')
      return
    }

    // Calculate total price
    const totalPrice = this.calculateTotalPrice(defaultVariants)
    
    // Calculate discount
    const { finalPrice, discountPercent } = this.calculateDiscount(totalPrice, defaultVariants.length)
    
    // Display price
    this.displayPrice(totalPrice, finalPrice, discountPercent)
  }

  private extractDefaultVariants(): DefaultVariantInfo[] {
    const defaultVariants: DefaultVariantInfo[] = []

    // Iterate through all categories and extract default variants from all products
    for (const category of this.kitData.content) {
      for (const product of category.products) {
        const defaultVariant = product.variants.find(variant => variant.default && variant.enabled && variant.fakeSoldOut === false)


        if (defaultVariant) {
          let batchVariant: any = null
          this.batchData.forEach(p => {
            if (p.id?.toString() === product.productId) {
              batchVariant = p.variants.find((variant: any) => variant.id?.toString() === defaultVariant.variantId)
            }
          })

          const isAvailable = batchVariant?.available

          if (isAvailable) {
            // Get price from batch data
            const price = batchVariant?.price || 0
            const quantity = parseInt(product.quantity) || 1

            // Only add if price is valid
            if (price > 0) {
              defaultVariants.push({
                variantId: defaultVariant.variantId,
                price: price,
                quantity: quantity,
                productId: product.productId,
                productHandle: product.productHandle
              })
            }
          }
        }
      }
    }

    return defaultVariants
  }

  private calculateTotalPrice(defaultVariants: DefaultVariantInfo[]): number {
    return defaultVariants.reduce((total, variant) => {
      return total + (variant.price * variant.quantity)
    }, 0)
  }

  private calculateDiscount(totalPrice: number, totalItems: number): { finalPrice: number, discountPercent: number } {
    if (!this.kitData.discount) {
      return { finalPrice: totalPrice, discountPercent: 0 }
    }

    const discountConfig = this.kitData.discount

    if (discountConfig.discountType === 'allProducts') {
      // For allProducts type, apply discount if all items are selected
      // Since we're showing defaults, we'll apply the discount
      const discountPercent = discountConfig.discount || 0
      const multiplier = (100 - discountPercent) / 100
      return {
        finalPrice: totalPrice * multiplier,
        discountPercent: discountPercent
      }
    } else if (discountConfig.discountType === 'tierDiscount' && discountConfig.tiers) {
      // Find the highest applicable tier
      const sortedTiers = [...discountConfig.tiers].sort((a, b) => b.minQuantity - a.minQuantity)
      
      for (const tier of sortedTiers) {
        if (totalItems >= tier.minQuantity) {
          const multiplier = (100 - tier.discount) / 100
          return {
            finalPrice: totalPrice * multiplier,
            discountPercent: tier.discount
          }
        }
      }
    }

    // No discount applies
    return { finalPrice: totalPrice, discountPercent: 0 }
  }

  private displayPrice(originalPrice: number, finalPrice: number, discountPercent: number): void {
    const originalPriceFormatted = this.formatPrice(originalPrice / 100)
    const finalPriceFormatted = this.formatPrice(finalPrice / 100)

    if (discountPercent > 0) {
      // Show discounted price
      this.priceContainer.innerHTML = /*html*/ `
        <span data-uc-recipe-display-price>
          <s>$${originalPriceFormatted}</s>
          $${finalPriceFormatted}
        </span>
      `
    } else {
      // Show regular price
      this.priceContainer.innerHTML = /*html*/ `
        <span data-uc-recipe-display-price>
          $${originalPriceFormatted}
        </span>
      `
    }
  }

  private formatPrice(price: number): string {
    return price.toFixed(2).replace(/\.00$/, '')
  }

  private async submitBundle(): Promise<void> {
    const defaultVariants = this.extractDefaultVariants()
    
    if (defaultVariants.length === 0) {
      console.warn('No variants to add to bundle')
      return
    }

    const bundleId = this.kitData.parent.variantId
    const time = new Date().getTime()

    // Create cart items from default variants
    const items: CartItem[] = defaultVariants
      .filter(variant => variant.price > 0) // Only include variants with valid prices
      .map(variant => ({
        id: variant.variantId,
        quantity: variant.quantity,
        properties: {
          _bundleId: bundleId,
          _quickAdd: 'true',
          _uniqueId: time.toString(),
          _version: 'v3'
        }
      }))

    if (items.length === 0) {
      console.warn('No valid items to add to bundle')
      return
    }

    try {
      // Show loading state
      this.setLoadingState(true)

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      })

      if (response.ok) {
        console.log('Bundle added to cart successfully')
        // Trigger cart update events here if needed
        this.dispatchCartUpdateEvent()
      } else {
        const error = await response.json()
        console.error('Error adding bundle to cart:', error)
        this.showError('Error adding bundle to cart')
      }
    } catch (error) {
      console.error('Error adding bundle to cart:', error)
      this.showError('Network error occurred')
    } finally {
      this.setLoadingState(false)
    }
  }

  private setLoadingState(loading: boolean): void {
    const spinner = this.submitButton.querySelector('[data-uc-loading-overlay-spinner]') as HTMLElement
    const buttonText = this.submitButton.querySelector(':not([data-uc-loading-overlay-spinner])') as HTMLElement

    if (loading) {
      this.submitButton.disabled = true
      spinner?.classList.remove('hidden')
      if (buttonText) buttonText.style.visibility = 'hidden'
    } else {
      this.submitButton.disabled = false
      spinner?.classList.add('hidden')
      if (buttonText) buttonText.style.visibility = 'visible'
    }
  }

  private showError(message: string): void {
    // You can implement error display logic here
    console.error(message)
    // Could show a toast notification or update UI to show error
  }

  private dispatchCartUpdateEvent(): void {
    // Dispatch custom event to notify other components of cart update
    const event = new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { source: 'bundle-quick-add' }
    })
    this.dispatchEvent(event)
  }

  // Public method to recalculate price (useful if kit data changes)
  public recalculatePrice(): void {
    this.updatePrice()
  }

  // Public method to update kit data
  public updateKitData(newKitData: KitData): void {
    this.kitData = newKitData
    this.updatePrice()
  }
} 